import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import StatusPill from "./StatusPill";
import StatCardRow from "./StatCardRow";
import ChipGrid from "./ChipGrid";
import DashboardTable from "./DashboardTable";
import TableLoading from "./TableLoading";
import Pager from "./Pager";
import { toPlainText } from "../utils/plainText";
import "../reportDashboard.scss";

const RecordList = ({ listTitle, listColumns, listRows }) => (
  <>
    <div className="rdModal__listTitle">{listTitle}</div>
    <div className="rdTable__scroll">
      <table className="rdTable">
        <thead>
          <tr>
            {listColumns.map((c) => (
              <th key={c.field}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {listRows.map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <tr key={i}>
              {listColumns.map((c) => (
                <td key={c.field}>{toPlainText(row[c.field]) ?? "—"}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
);

// One named block inside a modal, beyond the top fields/warning/list — used by
// modals with more than one titled section (Lesson/Course assignment records,
// Safety Meeting instructor roster + attendees, Vehicle/Equipment "Safety
// Activity", the Users profile). `section.type` selects the body:
//   fields     — { fields: [{label,value}] }
//   statRow    — { items, columns } (rendered via StatCardRow, inner)
//   chips      — { items, emptyLabel, rowCount?, page?, pageSize?, onPageChange?, loading? }
//   table      — { columns, rows, serverMode?, rowCount?, page?, onPageChange?, loading?, pageSize?, emptyLabel? }
//   note       — { text }
//   list       — { listColumns, listRows } (same shape as the top-level list slot)
//   cards      — { rows: [{ title, pill, meta, note }], emptyLabel, rowCount?, page?,
//                pageSize?, onPageChange?, loading? } — Reporter Case History /
//                linked-cases style: title left, pill + optional meta right, note
//                (muted) below.
//                `chips`/`cards` pagination is OPTIONAL: it only renders (via the
//                shared Pager) when the section supplies `rowCount` — callers with a
//                small, un-paged list (e.g. a fixed roster) simply omit it and the
//                section renders exactly as it did before pagination existed. When
//                supplied, `page`/`pageSize`/`onPageChange`/`loading` should be the
//                matching fields straight off a usePaginatedList() result, same as the
//                `table` section above already does.
//   bullets    — { items: [string], emptyLabel } — a plain <ul>, e.g. Included Lessons.
//   roster     — { rows: [{ name, completed }], emptyLabel } — name left,
//                Completed/Not Completed pill right, single line.
//   legendRows — { rows: [{ label, value, tone, note }] } — .rdKeyRow + .rdDot,
//                with an optional indented note line (e.g. "includes N from Observations").
const Section = ({ section }) => (
  <div className="rdModal__section">
    {section.title && <div className="rdModal__listTitle">{section.title}</div>}
    {section.type === "fields" && (
      <div className="rdModal__fields">
        {(section.fields || []).map((f) => (
          <div key={f.label}>
            <div className="rdModal__fieldLabel">{f.label}</div>
            <div className="rdModal__fieldValue">{toPlainText(f.value) ?? "—"}</div>
          </div>
        ))}
      </div>
    )}
    {section.type === "statRow" && (
      <StatCardRow items={section.items} columns={section.columns || section.items.length} inner />
    )}
    {section.type === "chips" && (
      <>
        {/* Same first-load guard as "table"/"cards" above — only meaningful
            once a section actually has a `rowCount` (server-paged); a
            fixed, un-paged chip list (e.g. a meeting's attendees) has no
            `loading` of its own and renders straight through. */}
        {section.rowCount != null && section.loading && !(section.items && section.items.length > 0) ? (
          <TableLoading />
        ) : (
          <ChipGrid items={section.items} emptyLabel={section.emptyLabel} />
        )}
        {section.rowCount != null && (
          <Pager
            page={section.page}
            pageSize={section.pageSize}
            rowCount={section.rowCount}
            onPageChange={section.onPageChange}
            loading={section.loading}
          />
        )}
      </>
    )}
    {section.type === "table" &&
      // An empty `rows` is also what the first, still-loading fetch looks like,
      // so the loading check has to come first — otherwise opening the modal
      // reads as "No records" until the rows land (CredentialsSection's
      // Lessons/Courses assignment drill-down did exactly that).
      (section.loading && !(section.rows && section.rows.length > 0) ? (
        <TableLoading />
      ) : section.rows && section.rows.length > 0 ? (
        <DashboardTable
          columns={section.columns}
          rows={section.rows}
          serverMode={section.serverMode}
          rowCount={section.rowCount}
          page={section.page}
          onPageChange={section.onPageChange}
          loading={section.loading}
          pageSize={section.pageSize}
        />
      ) : (
        <div className="rdEmpty">{section.emptyLabel || "No records"}</div>
      ))}
    {section.type === "note" && <div className="rdCard__hint">{section.text}</div>}
    {section.type === "cards" && (
      <>
        {/* Same first-load guard as the "table" type above: an empty `rows`
            is also what the still-loading first fetch looks like, so it has
            to be checked before falling to the empty-state message
            (OverviewSection's Top Case Reporters drill-down read as "No
            cases found" until the page landed). */}
        {section.loading && !(section.rows && section.rows.length > 0) ? (
          <TableLoading />
        ) : section.rows && section.rows.length > 0 ? (
          <div className="rdModal__cards">
            {section.rows.map((row, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div className="rdModal__card" key={i}>
                <div className="rdModal__cardHead">
                  <span className="rdModal__cardTitle">{toPlainText(row.title)}</span>
                  {row.pill && <StatusPill status={row.pill} />}
                  {row.meta && <span className="rdModal__cardMeta">{toPlainText(row.meta)}</span>}
                </div>
                {row.note && (
                  <div className="rdModal__cardNote">{toPlainText(row.note)}</div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="rdEmpty">{section.emptyLabel || "No records"}</div>
        )}
        {section.rowCount != null && (
          <Pager
            page={section.page}
            pageSize={section.pageSize}
            rowCount={section.rowCount}
            onPageChange={section.onPageChange}
            loading={section.loading}
          />
        )}
      </>
    )}
    {section.type === "bullets" &&
      (section.items && section.items.length > 0 ? (
        <ul className="rdBullets">
          {section.items.map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <li key={i}>{toPlainText(item)}</li>
          ))}
        </ul>
      ) : (
        <div className="rdEmpty">{section.emptyLabel || "No records"}</div>
      ))}
    {section.type === "roster" &&
      (section.rows && section.rows.length > 0 ? (
        <div className="rdModal__cards">
          {section.rows.map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="rdModal__card rdModal__card--row" key={i}>
              <span className="rdModal__cardTitle">{toPlainText(row.name)}</span>
              <StatusPill status={row.completed ? "Completed" : "Not Completed"} />
            </div>
          ))}
        </div>
      ) : (
        <div className="rdEmpty">{section.emptyLabel || "No records"}</div>
      ))}
    {section.type === "legendRows" && (
      <div className="rdModal__legendRows">
        {(section.rows || []).map((row, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i}>
            <div className="rdKeyRow">
              <span className="rdKeyRow__label">
                <span className={`rdDot is-${row.tone || "muted"}`} />
                {toPlainText(row.label)}
              </span>
              <span className={`rdKeyRow__value is-${row.tone || "muted"}`}>
                {toPlainText(row.value)}
              </span>
            </div>
            {row.note && (
              <div className="rdModal__legendNote">{toPlainText(row.note)}</div>
            )}
          </div>
        ))}
      </div>
    )}
    {section.type === "list" &&
      (section.listRows && section.listRows.length > 0 ? (
        <div className="rdTable__scroll">
          <table className="rdTable">
            <thead>
              <tr>
                {section.listColumns.map((c) => (
                  <th key={c.field}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {section.listRows.map((row, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <tr key={i}>
                  {section.listColumns.map((c) => (
                    <td key={c.field}>{toPlainText(row[c.field]) ?? "—"}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rdEmpty">{section.emptyLabel || "No records"}</div>
      ))}
  </div>
);

// Generic record detail popup — used for every "click a row, see that one
// record's detail" drill-down (Case/Observation/Reprimand timeline items,
// Credential alert/type/issuing-body rows, Project rows, Lesson/Course/Safety
// Meeting rows, Vehicle/Equipment rows, User profiles). `sections` is an
// optional ordered list of additional named blocks beyond fields/warning/list
// — see `Section` above for the shapes it accepts.
// `bodyClass` is an opt-in class on the dialog body, for a caller whose content is
// prose rather than record fields — the Safety Score method modal passes
// "rdModal__body--prose" to size its own paragraph and rule list at the dashboard's
// 12px body scale. Omitted by every record drill-down, which keeps the default
// typography of the section types untouched.
const RecordModal = ({ open, onClose, title, statusPill, fields, warning, listTitle, listColumns, listRows, sections, bodyClass }) => (
  // v26's .modal is 680px wide; MUI's maxWidth="sm" caps at 600, so the width
  // comes from .rdModal__paper instead (see reportDashboard.scss).
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ className: "rdModal__paper" }}>
    <DialogTitle className="rdModal__title">
      <span className="rdModal__titleText">{toPlainText(title)}</span>
      {statusPill && <StatusPill status={statusPill} />}
      <IconButton className="rdModal__close" onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    {/* --tight when the modal opens straight into a section instead of a field
        pair: the title's 18px bottom padding plus DialogContent's own 20px top
        otherwise stack into a ~38px blank band above what is often small caption
        text (the Safety Score method modal). A field pair fills that space with a
        label, so the default padding stays for every other caller. */}
    <DialogContent
      className={[fields && fields.length > 0 ? null : "rdModal__content--tight", bodyClass]
        .filter(Boolean)
        .join(" ")}
    >
      {/* Rendered only when there are fields: the grid carries a 14px bottom
          margin, so an empty one left a blank band under the title for callers
          that open with a section instead of a field pair (the Safety Score
          method modal). */}
      {fields && fields.length > 0 && (
        <div className="rdModal__fields">
          {fields.map((f) => (
            <div key={f.label}>
              <div className="rdModal__fieldLabel">{f.label}</div>
              <div className="rdModal__fieldValue">{toPlainText(f.value) ?? "—"}</div>
            </div>
          ))}
        </div>
      )}

      {warning && (
        <div className={`rdNotice rdNotice--${warning.tone || "warning"}`}>
          {warning.text}
        </div>
      )}

      {(sections || []).map((section, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <Section key={section.title || i} section={section} />
      ))}

      {listRows && listRows.length > 0 && (
        <RecordList listTitle={listTitle} listColumns={listColumns} listRows={listRows} />
      )}
    </DialogContent>
  </Dialog>
);

export default RecordModal;
